import React, { useCallback, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { v4 as uuidv4 } from 'uuid';
import MusicNoteRoundedIcon from '@mui/icons-material/MusicNoteRounded';
import ControlPointDuplicateRoundedIcon from '@mui/icons-material/ControlPointDuplicateRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import actions from '@src/redux/actions';
import useFeatureFlags from '@src/hooks/useFeatureFlags';
import useTimeline from '@src/hooks/useTimeline';
import useTimelineDrag from '@src/hooks/useTimelineDrag';
import { FEATURE_KEYS } from '@src/configs/featureKeys';
import { HISTORY_LABEL, HISTORY_SCOPE } from '@src/constants/history';
import AppendButton from '@src/new-components/Timeline/AppendButton';
import InsertButton from '@src/new-components/Timeline/InsertButton';
import ClipMenu from '@src/new-components/Timeline/ClipMenu';
import BackgroundMusic from '@src/containers/BackgroundMusic';
import { loadAudioDurationFromUrl } from '@src/services/audio';
import { convertSecondsToSubtitleTime } from '@src/utils/time';
import {
  clampMove,
  clampTrimEnd,
  clampTrimStart,
  computeEdgeScrollVelocity,
  neighborBounds,
  snapSec,
} from '@src/services/timeline';
import {
  APPEND_GAP_PX,
  CLIP_MIN_DURATION_SEC,
  SNAP_THRESHOLD_PX,
} from '@src/constants/timeline';
import { VIDEO_DUBBING_MAX_DURATION_SECONDS } from '@src/constants/transcript';
import MusicClip from './MusicClip';
import { StyledAppendWrap, StyledDragTooltip, StyledLane } from './index.style';

// Level a newly added clip starts at, on the picker's 0..100 scale.
const DEFAULT_MUSIC_VOLUME = 100;

// BGM can't extend past the project's max length — the same 90-minute cap the
// video has, so no music sits beyond where the video can reach.
const MAX_BGM_END_SEC = VIDEO_DUBBING_MAX_DURATION_SECONDS;

// A gap narrower than this has no room for the insert button, so it gets none.
const INSERT_SLOT_MIN_PX = 40;

// Adding always starts from a blank selection. Required (the picker reads
// `.link` off it unguarded) and kept module-level: it sits in an effect's
// dependencies there, so a fresh object each render would re-run it.
const NO_CURRENT_MUSIC = {};

/**
 * Background-music track. Picking a track appends a clip; each clip shows its
 * waveform, can be dragged to move (clips don't overlap), selected (click) to
 * trim its edges, and has a right-click menu (duplicate / replace / delete).
 */
const MusicTrack = ({ isLocked = false, isPlaying = false }) => {
  const { t } = useTranslation();
  const { secToPx, pxToSec, visibleRange, viewportRef, scrollViewportBy } =
    useTimeline();
  const dispatch = useDispatch();
  const musicClips = useSelector((state) => state.project.musicClips);
  const user = useSelector((state) => state.auth.user);

  // Record BGM edits into the dubbing undo/redo stack only when the feature is
  // on — same flag and scope the voice track uses, so one Undo/Redo (button or
  // shortcut) walks back through both voice and music changes together.
  const { getFeatureValue } = useFeatureFlags();
  const undoRedoEnabled = getFeatureValue(
    FEATURE_KEYS.PROJECT_DUBBING_UNDO_REDO,
    { userId: user?.id, email: user?.email },
  );
  // Same flag as the voice track's insert, so both lanes turn on together.
  const insertBlockEnabled = getFeatureValue(
    FEATURE_KEYS.PROJECT_DUBBING_INSERT_BLOCK,
    { userId: user?.id, email: user?.email },
  );
  const historyOptions = useCallback(
    (label) =>
      undoRedoEnabled
        ? { history: { commit: true, scope: HISTORY_SCOPE.DUBBING, label } }
        : {},
    [undoRedoEnabled],
  );

  // Every clip edit is off while a download/export runs OR while the project is
  // playing — the track must not change under the audio the user is hearing.
  // Transport and zoom stay live either way.
  const isEditLocked = isLocked || isPlaying;

  const [pickerOpen, setPickerOpen] = useState(false);
  // The clip being replaced (picker updates it instead of adding), or null.
  const [replaceTarget, setReplaceTarget] = useState(null);
  // The gap { startSec, endSec } a new clip drops into, set by an insert button;
  // the picker then places the pick there. The leading gap starts at 0.
  const [insertGap, setInsertGap] = useState(null);
  const [menu, setMenu] = useState(null); // { position: { top, left }, clip }
  // The clip whose trim handles are shown, or null.
  const [selectedClipId, setSelectedClipId] = useState(null);
  // Live position of the clip being dragged/trimmed, so it previews before it's
  // committed to redux on release. { id, startSec, endSec } or null.
  const [drag, setDrag] = useState(null);
  // Measured song length per clip (reported by MusicClip once decoded), used to
  // cap trimming so a clip can't play past the end of its audio.
  const durationsRef = useRef({});
  const handleClipDuration = useCallback((clipId, duration) => {
    durationsRef.current[clipId] = duration;
  }, []);

  // The append button sits just past the last clip. While that clip is being
  // moved/trimmed, follow its live draft end so the button moves with it instead
  // of staying pinned — otherwise the growing clip slides under the button.
  const lastClip = musicClips.length ? musicClips[musicClips.length - 1] : null;
  const lastEndSec =
    lastClip && drag?.id === lastClip.id ? drag.endSec : lastClip?.endSec ?? 0;

  const closeMenu = useCallback(() => setMenu(null), []);
  const closePicker = useCallback(() => {
    setPickerOpen(false);
    setReplaceTarget(null);
    setInsertGap(null);
  }, []);

  // The picker reports the chosen track; its length sizes the clip, so measure
  // before applying. In replace mode the clip keeps its start and just re-sizes
  // from the new audio; otherwise a new clip is appended after the last one.
  const handleSelectMusic = useCallback(
    async (name, link, volume) => {
      // The picker reports nothing when no track is selected — nothing to do.
      if (!link) return;
      const duration = await loadAudioDurationFromUrl(link);
      if (!duration) return;
      if (replaceTarget) {
        const index = musicClips.findIndex((c) => c.id === replaceTarget.id);
        if (index === -1) return;
        const clip = musicClips[index];
        // A longer replacement must not overrun the next clip (or the 90-min
        // cap), so clamp its end to whichever comes first.
        const maxEndSec = Math.min(
          musicClips[index + 1]?.startSec ?? Infinity,
          MAX_BGM_END_SEC,
        );
        dispatch(
          actions.project.updateMusicClip(
            clip.id,
            {
              name,
              link,
              volume,
              // A new song plays from its start again.
              trimStart: 0,
              endSec: Math.min(clip.startSec + duration, maxEndSec),
            },
            historyOptions(HISTORY_LABEL.REPLACE_MUSIC_CLIP),
          ),
        );
        return;
      }
      if (insertGap) {
        // Clip fills from the gap's left edge, tail cut at the right edge (or
        // the cap). addMusicClip sorts, so a leading clip lands first.
        const { startSec } = insertGap;
        const gapEndSec = Math.min(insertGap.endSec, MAX_BGM_END_SEC);
        dispatch(
          actions.project.addMusicClip(
            {
              id: uuidv4(),
              name,
              link,
              volume,
              trimStart: 0,
              startSec,
              endSec: Math.min(startSec + duration, gapEndSec),
            },
            historyOptions(HISTORY_LABEL.ADD_MUSIC_CLIP),
          ),
        );
        return;
      }
      // No room left before the 90-min cap — don't append past it.
      if (lastEndSec >= MAX_BGM_END_SEC) return;
      dispatch(
        actions.project.addMusicClip(
          {
            id: uuidv4(),
            name,
            link,
            volume,
            // Offset into the song where the clip starts playing (skip-intro trim).
            trimStart: 0,
            startSec: lastEndSec,
            // Cut the tail so the clip can't run past the cap.
            endSec: Math.min(lastEndSec + duration, MAX_BGM_END_SEC),
          },
          historyOptions(HISTORY_LABEL.ADD_MUSIC_CLIP),
        ),
      );
    },
    [
      dispatch,
      replaceTarget,
      insertGap,
      lastEndSec,
      musicClips,
      historyOptions,
    ],
  );

  const handleDuplicate = useCallback(() => {
    const { clip } = menu;
    const duration = clip.endSec - clip.startSec;
    // Duplicating pushes every later clip right by the copy's length, extending
    // the track to `lastEndSec + duration` — skip it if that passes the cap.
    if (lastEndSec + duration > MAX_BGM_END_SEC) {
      closeMenu();
      return;
    }
    dispatch(
      actions.project.duplicateMusicClip(
        clip.id,
        {
          ...clip,
          id: uuidv4(),
          startSec: clip.endSec,
          endSec: clip.endSec + duration,
        },
        historyOptions(HISTORY_LABEL.DUPLICATE_MUSIC_CLIP),
      ),
    );
    closeMenu();
  }, [menu, lastEndSec, dispatch, closeMenu, historyOptions]);

  const handleReplace = useCallback(() => {
    setReplaceTarget(menu.clip);
    setPickerOpen(true);
    closeMenu();
  }, [menu, closeMenu]);

  const handleDelete = useCallback(() => {
    dispatch(
      actions.project.deleteMusicClip(
        menu.clip.id,
        historyOptions(HISTORY_LABEL.DELETE_MUSIC_CLIP),
      ),
    );
    closeMenu();
  }, [menu, dispatch, closeMenu, historyOptions]);

  // Scroll-aware pointer→seconds and edge auto-scroll for the drag hook — same
  // wiring the voice track uses, so a music clip drags identically.
  const toSec = useCallback(
    (clientX) => {
      const el = viewportRef.current;
      if (!el) return 0;
      const rect = el.getBoundingClientRect();
      return pxToSec(clientX - rect.left + el.scrollLeft);
    },
    [viewportRef, pxToSec],
  );

  const autoScroll = useMemo(
    () => ({
      getVelocity: (clientX) => {
        const el = viewportRef.current;
        if (!el) return 0;
        const rect = el.getBoundingClientRect();
        return computeEdgeScrollVelocity(clientX, rect.left, rect.width);
      },
      scrollBy: (deltaPx) => scrollViewportBy(deltaPx),
    }),
    [viewportRef, scrollViewportBy],
  );

  const { beginDrag } = useTimelineDrag({ toSec, autoScroll });

  // SNAP_THRESHOLD_PX in seconds at the current zoom, so a dragged clip butts
  // flush against a neighbour instead of stopping a pixel short.
  const snapThresholdSec = useMemo(() => {
    const pxPerSec = secToPx(1) - secToPx(0);
    return pxPerSec > 0 ? SNAP_THRESHOLD_PX / pxPerSec : 0;
  }, [secToPx]);

  // Drag a clip's body to move it, clamped between its neighbours (clips don't
  // overlap). A press with no real movement is a no-op — the clip menu opens on
  // right-click (onContextMenu), not click, so it never collides with the drag.
  const handleClipPointerDown = useCallback(
    (event, clip) => {
      if (isEditLocked) return;
      const index = musicClips.findIndex((c) => c.id === clip.id);
      const { minStartSec, maxEndSec } = neighborBounds(
        musicClips,
        index,
        MAX_BGM_END_SEC,
      );
      const snapCandidates = [minStartSec, maxEndSec];
      let next = { startSec: clip.startSec, endSec: clip.endSec };
      beginDrag(event, {
        id: clip.id,
        onMove: (deltaSec) => {
          // Snap whichever edge lands closest to a neighbour boundary, then move
          // by that snapped delta so the clip butts flush.
          const rawStart = clip.startSec + deltaSec;
          const rawEnd = clip.endSec + deltaSec;
          const startShift =
            snapSec(rawStart, snapCandidates, snapThresholdSec) - rawStart;
          const endShift =
            snapSec(rawEnd, snapCandidates, snapThresholdSec) - rawEnd;
          const snapShift =
            startShift !== 0 &&
            (endShift === 0 || Math.abs(startShift) <= Math.abs(endShift))
              ? startShift
              : endShift;
          next = clampMove({
            startSec: clip.startSec,
            endSec: clip.endSec,
            deltaSec: deltaSec + snapShift,
            minStartSec,
            maxEndSec,
          });
          // Moving keeps the song offset; carry it so the waveform doesn't reset.
          setDrag({ id: clip.id, ...next, trimStart: clip.trimStart });
        },
        onEnd: () => {
          setDrag(null);
          dispatch(
            actions.project.updateMusicClip(
              clip.id,
              {
                startSec: next.startSec,
                endSec: next.endSec,
              },
              historyOptions(HISTORY_LABEL.MOVE_MUSIC_CLIP),
            ),
          );
        },
        // A press with no real movement selects the clip (shows its trim
        // handles). Right-click still opens the menu via onContextMenu.
        onClick: () => setSelectedClipId(clip.id),
      });
    },
    [
      musicClips,
      beginDrag,
      snapThresholdSec,
      dispatch,
      historyOptions,
      isEditLocked,
    ],
  );

  // Drag a trim handle. `end` cuts the tail. `start` skips the intro: startSec
  // and trimStart move together (right edge fixed), so the kept audio stays put
  // and a later part of the song shows. Clamped to neighbours / min length / the
  // song, with snapping.
  const handleTrimHandlePointerDown = useCallback(
    (event, clipId, edge) => {
      if (isEditLocked) return;
      const index = musicClips.findIndex((c) => c.id === clipId);
      const clip = musicClips[index];
      if (!clip) return;
      const { minStartSec, maxEndSec } = neighborBounds(
        musicClips,
        index,
        MAX_BGM_END_SEC,
      );
      const songDuration = durationsRef.current[clipId];
      const trimStart = clip.trimStart || 0;
      const snapCandidates = [minStartSec, maxEndSec];
      let next = { startSec: clip.startSec, endSec: clip.endSec, trimStart };
      beginDrag(event, {
        id: clipId,
        onMove: (deltaSec) => {
          if (edge === 'end') {
            // Can't play past the end of the song.
            const range = clampTrimEnd({
              startSec: clip.startSec,
              rawEndSec: snapSec(
                clip.endSec + deltaSec,
                snapCandidates,
                snapThresholdSec,
              ),
              maxEndSec,
              minDurationSec: CLIP_MIN_DURATION_SEC,
              maxDurationSec: songDuration
                ? songDuration - trimStart
                : Infinity,
            });
            next = { ...range, trimStart };
          } else {
            // Can't skip before the song's start (trimStart >= 0).
            const range = clampTrimStart({
              endSec: clip.endSec,
              rawStartSec: snapSec(
                clip.startSec + deltaSec,
                snapCandidates,
                snapThresholdSec,
              ),
              minStartSec: Math.max(minStartSec, clip.startSec - trimStart),
              minDurationSec: CLIP_MIN_DURATION_SEC,
              maxDurationSec: Infinity,
            });
            next = {
              ...range,
              trimStart: trimStart + (range.startSec - clip.startSec),
            };
          }
          setDrag({ id: clipId, ...next });
        },
        onEnd: () => {
          setDrag(null);
          dispatch(
            actions.project.updateMusicClip(
              clipId,
              {
                startSec: next.startSec,
                endSec: next.endSec,
                trimStart: next.trimStart,
              },
              historyOptions(HISTORY_LABEL.TRIM_MUSIC_CLIP),
            ),
          );
        },
      });
    },
    [
      musicClips,
      beginDrag,
      snapThresholdSec,
      dispatch,
      historyOptions,
      isEditLocked,
    ],
  );

  const handleInsertClick = useCallback((gap) => {
    setInsertGap(gap);
    setPickerOpen(true);
  }, []);

  // An insert slot per gap (leading + between clips), clamped to the viewport.
  // Off while disabled/locked/playing/dragging, or where the gap is too small.
  const insertSlots = useMemo(() => {
    if (!insertBlockEnabled || isEditLocked || drag) return [];
    const viewport = viewportRef.current;
    const windowLeftPx = viewport
      ? viewport.scrollLeft
      : secToPx(visibleRange.fromSec);
    const windowRightPx = viewport
      ? viewport.scrollLeft + viewport.clientWidth
      : secToPx(visibleRange.toSec);
    const slotIn = (startSec, endSec) => {
      if (endSec - startSec < CLIP_MIN_DURATION_SEC) return null;
      const left = Math.max(secToPx(startSec), windowLeftPx);
      const right = Math.min(secToPx(endSec), windowRightPx);
      const width = right - left;
      if (width < INSERT_SLOT_MIN_PX) return null;
      return { startSec, endSec, left, width };
    };

    const slots = [];
    const first = musicClips[0];
    if (first && first.startSec > 0) {
      const slot = slotIn(0, first.startSec);
      if (slot) slots.push(slot);
    }
    musicClips.forEach((clip, index) => {
      const nextClip = musicClips[index + 1];
      if (!nextClip) return;
      const slot = slotIn(clip.endSec, nextClip.startSec);
      if (slot) slots.push(slot);
    });
    return slots;
  }, [
    insertBlockEnabled,
    musicClips,
    visibleRange,
    isEditLocked,
    drag,
    secToPx,
    viewportRef,
  ]);

  return (
    <StyledLane
      // Clicking the empty lane (not a clip) clears the selection.
      onClick={(event) => {
        if (event.target === event.currentTarget) setSelectedClipId(null);
      }}
    >
      {musicClips.map((clip) => {
        // While this clip is dragging/trimming, render from the live draft.
        const pos = drag?.id === clip.id ? drag : clip;
        const left = secToPx(pos.startSec);
        return (
          <MusicClip
            key={clip.id}
            clip={clip}
            dragging={drag?.id === clip.id}
            selected={selectedClipId === clip.id}
            trimStart={pos.trimStart || 0}
            style={{ left, width: Math.max(secToPx(pos.endSec) - left, 0) }}
            onPointerDown={(event) => handleClipPointerDown(event, clip)}
            onHandlePointerDown={handleTrimHandlePointerDown}
            onDuration={handleClipDuration}
            isLocked={isLocked}
            isPlaying={isPlaying}
            onContextMenu={(event) => {
              event.preventDefault();
              // Every menu item edits the track, so it stays shut while locked
              // or playing.
              if (isEditLocked) return;
              setMenu({
                position: { top: event.clientY, left: event.clientX },
                clip,
              });
            }}
          />
        );
      })}

      {drag && (
        <StyledDragTooltip left={secToPx((drag.startSec + drag.endSec) / 2)}>
          {`${convertSecondsToSubtitleTime(
            drag.startSec,
          )} → ${convertSecondsToSubtitleTime(drag.endSec)}`}
        </StyledDragTooltip>
      )}

      {insertSlots.map(({ startSec, endSec, left, width }) => (
        <InsertButton
          key={`insert-${startSec}`}
          title={t('addBackgroundMusic')}
          left={left}
          width={width}
          onClick={() => handleInsertClick({ startSec, endSec })}
        />
      ))}

      <StyledAppendWrap
        data-timeline-append
        left={secToPx(lastEndSec) + APPEND_GAP_PX}
      >
        <AppendButton
          label={t('addBackgroundMusic')}
          onClick={() => setPickerOpen(true)}
          // Too little room before the 90-min cap to fit even a minimal clip.
          disabled={
            isEditLocked || MAX_BGM_END_SEC - lastEndSec < CLIP_MIN_DURATION_SEC
          }
        />
      </StyledAppendWrap>

      <ClipMenu
        anchorPosition={menu?.position}
        open={Boolean(menu)}
        onClose={closeMenu}
        items={
          menu
            ? [
                {
                  key: 'duplicate',
                  label: t('createCopy'),
                  icon: <ControlPointDuplicateRoundedIcon fontSize="small" />,
                  onClick: handleDuplicate,
                  // A copy needs its full length; disable when that would pass
                  // the 90-min cap (duplicating can't cut the tail like adding).
                  disabled:
                    lastEndSec + (menu.clip.endSec - menu.clip.startSec) >
                    MAX_BGM_END_SEC,
                },
                {
                  key: 'replace',
                  label: t('replaceBackgroundMusic'),
                  icon: <MusicNoteRoundedIcon fontSize="small" />,
                  onClick: handleReplace,
                },
                {
                  key: 'delete',
                  label: t('delete'),
                  icon: <DeleteOutlineRoundedIcon fontSize="small" />,
                  onClick: handleDelete,
                },
              ]
            : []
        }
      />

      <BackgroundMusic
        open={pickerOpen}
        currentBackgroundMusic={replaceTarget || NO_CURRENT_MUSIC}
        onClose={closePicker}
        onChange={handleSelectMusic}
        showUiChangeVolume
        volume={replaceTarget?.volume ?? DEFAULT_MUSIC_VOLUME}
        max={100}
      />
    </StyledLane>
  );
};

// Memoized — its only props are two stable booleans, so a parent re-render (e.g.
// the playhead crossing a block) doesn't re-render it.
export default React.memo(MusicTrack);
